// auditWpObjectiveService.ts
import { BASE_AUDIT_API, fetchAPI } from '@/services/api';
import { buildPageQueryParams, ResponsePage } from '@/services/page.interface';

// ==================== VO 定义 ====================

/** 审计工作底稿目标视图对象 (对应后端 AuditWpObjectiveVO) */
export interface AuditWpObjectiveVO {
    id?: string;
    parentId?: string;
    code?: string[];
    symbol?: string;
    content?: string;
    createUserId?: number;
    isActive?: number;
    createTime?: string;
    updateTime?: string;
    updateUserId?: number;
    description?: string;
    isDel?: number;
}

/** 内容和符号项 (对应后端 ContentsAndSymbol) */
export interface ContentsAndSymbol {
    id: string;
    code: string;
    symbol: string;
    content: string;
    parentId: string;
}

/** AuditWokeDoneVO (code + wd + dynamicWdMaps) */
export interface AuditWokeDoneVO {
    code: string;
    wd: ContentsAndSymbol[];
    dynamicWdMaps: Record<string, any>;
}

/** 分组子节点 (对应后端 ChildrenGroupVO，根据实际结构补充) */
export interface ChildrenGroupVO {
    // 请根据后端实际 ChildrenGroupVO 的字段补充，当前假设为空对象
    [key: string]: any;
}

/** 按代码分组的内容视图 (对应后端 AuditWpObjectiveByCodeVO) */
export interface AuditWpObjectiveByCodeVO {
    code: string;
    contents: ContentsAndSymbol[];
    children: ChildrenGroupVO;
}

// ==================== BO 定义 ====================

/** 更新/新增工作底稿的 BO (对应后端 UpdateObjectiveCodeBO) */
export interface UpdateObjectiveCodeBO {
    id?: string;               // 新增时可不传，更新时必传
    code?: string[];
    content?: string;
    symbol?: string;
    parentId?: string | null;
    updateUserId?: number;
    isDel?: number;
    isActive?: number;
    // isDel?
}

// ==================== API 路径 ====================
const base = `${BASE_AUDIT_API}/work-done`;

// ==================== 接口函数 ====================

/** 根据 ID 查询详情 */
export async function getById(id: string) {
    return fetchAPI<AuditWpObjectiveVO>(`${base}/getByIs/${id}`, {
        method: 'GET',
    });
}
/** 获取所有数据列表 (分页，按 parentId 升序) */
export async function getList(pageNumber: number = 1, pageSize: number = 10) {
    return fetchAPI<ResponsePage<AuditWpObjectiveVO>>(
        `${base}/getList?${buildPageQueryParams(pageNumber, pageSize).toString()}`,
        { method: 'GET' },
    );
}

/** 根据 code 获取内容和符号 (返回 AuditWokeDoneVO) */
export async function getCodeAndContents(code: string) {
    return fetchAPI<AuditWokeDoneVO>(`${base}/code-content/${encodeURIComponent(code)}`, {
        method: 'GET',
    });
}

/** 分页获取所有 code 对应的内容和符号 (支持分页) */
export async function getAllCodeWithSymbolAndContent(pageNumber: number = 1, pageSize: number = 10) {
    return fetchAPI<ResponsePage<AuditWpObjectiveByCodeVO>>(
        `${base}/code-all-content?${buildPageQueryParams(pageNumber, pageSize).toString()}`,
        { method: 'GET' },
    );
}

/** 逻辑删除 (根据 ID) */
export async function removeById(id: string) {
    return fetchAPI<boolean>(`${base}/remove/${id}`, {
        method: 'DELETE',
    });
}

/** 更新工作底稿 (根据 ID 更新 code、content 等) */
export async function updateCodeById(bo: UpdateObjectiveCodeBO) {
    return fetchAPI<boolean>(`${base}/updatById`, {
        method: 'POST',
        body: JSON.stringify(bo),
    });
}

/** 新增工作底稿 */
export async function add(bo: UpdateObjectiveCodeBO) {
    return fetchAPI<boolean>(`${base}/add`, {
        method: 'POST',
        body: JSON.stringify(bo),
    });
}

/** 根据 content 模糊查询分页列表 */
export async function listByContent(
    pageNumber: number = 1,
    pageSize: number = 10,
    content?: string,
) {
    const params = buildPageQueryParams(pageNumber, pageSize);
    if (content) {
        params.append('content', content);
    }
    return fetchAPI<ResponsePage<AuditWpObjectiveVO>>(
        `${base}/listByContent?${params.toString()}`,
        { method: 'GET' },
    );
}